"use client";

import { useEffect, useState } from "react";

import { sendPhoneCode } from "@/pages/sign-up/_helpers";
import { useForm } from "@/hooks";

type UseResendPhoneVerificationCodeReturn = {
  loading: boolean;
  timeout: number;
  text: string;
  handleResendCode: () => Promise<void>;
};

/**
 * useResendPhoneVerificationCode Hook
 *
 * Sends another SMS code and runs the 30-second resend cooldown.
 *
 * @returns Timer, button copy, and the resend handler
 */
const useResendPhoneVerificationCode =
  (): UseResendPhoneVerificationCodeReturn => {
    // Phone fields from the sign-up form so the resend hits the same number
    const { values } = useForm();
    const [loading, setLoading] = useState(false);
    const [timeout, setTimeoutValue] = useState(0);
    const [text, setText] = useState("Click to resend");

    // Ask the API for a new code, then start the cooldown on success
    const handleResendCode = async () => {
      setText("Re-sending...");
      setLoading(true);

      const isSent = await sendPhoneCode(
        `${String(values.phonePrefix ?? "")}${String(values.phoneNumber ?? "").replaceAll(" ", "")}`
      );

      if (isSent) {
        setTimeoutValue(1000 * 30);
        setText("Click to resend");
      } else {
        setText("Opps, something went wrong. Try again");
      }

      setLoading(false);
    };

    // Tick the remaining cooldown down once per second while it is active
    useEffect(() => {
      let interval: NodeJS.Timeout | null = null;

      if (timeout > 0) {
        interval = setInterval(() => {
          setTimeoutValue((prevTimeout) => Math.max(prevTimeout - 1000, 0));
        }, 1000);
      }

      return () => {
        if (interval) clearInterval(interval);
      };
    }, [timeout]);

    return {
      loading,
      timeout,
      text,
      handleResendCode,
    };
  };

export { useResendPhoneVerificationCode };
